import moment from "moment";

export const date2Helper = {
    ensureMoment(date) {
        if (moment.isMoment(date)) {
            return date;
        }

        if (date instanceof Date) {
            return moment(date);
        }

        if (typeof date === 'string' || typeof date === 'number') {
            const parsedDate = date2Helper.toDate(date);
            if (parsedDate) {
                return moment(parsedDate);
            }
        }

        return null; // Return null for invalid inputs
    },
    formatDate(date, format) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.format(format);
        }
        return null;
    },
    addDay(date, amount) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.add(amount, 'days');
        }
        return null;
    },
    addMonth(date, amount) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.add(amount, 'months');
        }
        return null;
    },
    addYear(date, amount) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.add(amount, 'years');
        }
        return null;
    },
    firstDayOfMonth(date) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.startOf('month').toDate();
        }
        return null;
    },
    lastDayOfMonth(date) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.endOf('month').toDate();
        }
        return null;
    },
    isDateEarlierThanNow(date) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.isBefore(date2Helper.ensureMoment(new Date()));
        }
        return false;
    },
    isSameDate(date1, date2) {
        const _date1 = date2Helper.ensureMoment(date1);
        const _date2 = date2Helper.ensureMoment(date2);
        if(_date1 && _date2) {
            return _date1.isSame(_date2);
        }
        return false;
    },
    isBeforeDate(date1, date2) {
        const _date1 = date2Helper.ensureMoment(date1);
        const _date2 = date2Helper.ensureMoment(date2);
        if(_date1 && _date2) {
            return _date1.isBefore(_date2);
        }
        return false;
    },
    isAfterDate(date1, date2) {
        const _date1 = date2Helper.ensureMoment(date1);
        const _date2 = date2Helper.ensureMoment(date2);
        if(_date1 && _date2) {
            return _date1.isAfter(_date2);
        }
        return false;
    },
    isBetweenDate(date, startDate, endDate) {
        const _date = date2Helper.ensureMoment(date);
        const _startDate = date2Helper.ensureMoment(startDate);
        const _endDate = date2Helper.ensureMoment(endDate);

        if(_date && _startDate && _endDate) {
            return _date.isBetween(_startDate, _endDate, null, '[]') || date2Helper.isSameDate(_date, _startDate) || date2Helper.isSameDate(_date, _endDate);
        }
        return false;
    },
    isDate(date) {
        const _date = this.ensureMoment(date);
        return _date !== null && _date.isValid();
    },
    isDateInstance(date) {
        return date instanceof Date;
    },
    isMoment(date) {
        return moment.isMoment(date);
    },
    toDate(date) {
        if (!date) {
            return null;
        }

        // If it's already a Date object, return it.
        if (date instanceof Date) {
            return date;
        }

        if (moment.isMoment(date)) {
            return date.toDate();
        }

        // If it's a number (timestamp), convert it.
        if (typeof date === 'number') {
            const m = moment(date);
            return m.isValid() ? m.toDate() : null;
        }

        // If it's not a string, we can't parse it.
        if (typeof date !== 'string') {
            return null;
        }

        // Try parsing with a list of common formats.
        const formats = [
            moment.ISO_8601, // Handles 'YYYY-MM-DDTHH:mm:ss.SSSZ' and 'YYYY-MM-DDTHH:mm:ss'
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD',
            'MM/DD/YYYY',
            'DD/MM/YYYY',
            'YYYY/MM/DD',
        ];

        for (const format of formats) {
            const m = moment(date, format, false); // Use strict parsing
            if (m.isValid()) {
                return m.toDate();
            }
        }

        // As a fallback, try Moment's flexible parsing.
        const fallbackMoment = moment(date);
        if (fallbackMoment.isValid()) {
            return fallbackMoment.toDate();
        }

        return null; // Return null if no format matches.
    },
    addMinute(date, amount) {
        const _date = date2Helper.ensureMoment(date);
        if(_date) {
            return _date.add(amount, 'minutes');
        }
        return null;
    },
    getDuration(startDate, endDate, asString = false) {
        let _startDate, _endDate;

        // Handle time strings like '23:30'
        if (typeof startDate === 'string' && startDate.match(/^\d{2}:\d{2}$/)) {
            _startDate = moment(startDate, 'HH:mm');
        } else {
            _startDate = date2Helper.ensureMoment(startDate);
        }

        if (typeof endDate === 'string' && endDate.match(/^\d{2}:\d{2}$/)) {
            _endDate = moment(endDate, 'HH:mm');
        } else {
            _endDate = date2Helper.ensureMoment(endDate);
        }

        if (!_startDate || !_endDate || !_startDate.isValid() || !_endDate.isValid()) {
            return null;
        }

        // Handle overnight case
        if (_endDate.isBefore(_startDate)) {
            _endDate.add(1, 'day');
        }

        const duration = moment.duration(_endDate.diff(_startDate));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        if (asString) {
            const parts = [];
            if (hours > 0) {
                parts.push(`${hours}h`);
            }
            if (minutes > 0) {
                parts.push(`${minutes}m`);
            }
            return parts.length > 0 ? parts.join(' ') : '0m';
        }

        return { hours, minutes };
    }
}
